import { ICON_PATHS, ICON_STROKE_WIDTH, ICON_VIEW_BOX, type IconName } from './icons.js';

export interface IconProps {
  name: IconName;
  /**
   * Rendered size in px. The prototype draws nav icons at 19–20 and inline icons
   * at 16–18; `20` is the default because the nav is the dominant use.
   */
  size?: number;
  /**
   * An accessible name. **Omit it for a decorative icon** — which is almost
   * always the right choice here, because these sit beside a text label or inside
   * an `IconButton` that already carries its own `label`. Supplying one in those
   * cases produces a double announcement ("Sources, Sources").
   *
   * Give it only when the icon is genuinely the sole carrier of meaning — and
   * per a11y.md §2 that situation is itself usually a design problem worth fixing
   * instead.
   */
  title?: string;
  className?: string;
}

/**
 * The icon primitive (design-system catalogue, Tier 1).
 *
 * Every icon in the product goes through here, so the house geometry — 24-grid,
 * 1.7px stroke, round caps, `currentColor` — is stated once and cannot drift per
 * call site. Colour is inherited: an icon inside an active nav item picks up the
 * accent automatically, which is how the prototype's icons behave.
 *
 * Added by the design-fidelity audit (F-2): the catalogue previously owned no
 * iconography at all, and its open "icon set not yet chosen" question is what
 * left both apps rendering Unicode dingbats in production.
 */
export function Icon({ name, size = 20, title, className }: IconProps) {
  const d = ICON_PATHS[name];
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox={ICON_VIEW_BOX}
      fill="none"
      stroke="currentColor"
      strokeWidth={ICON_STROKE_WIDTH}
      strokeLinecap="round"
      strokeLinejoin="round"
      // A decorative SVG must be hidden AND non-focusable: IE/legacy-Edge made
      // inline SVGs focusable by default, and some assistive tech still exposes
      // them, which puts an empty stop in the tab order.
      aria-hidden={title ? undefined : true}
      role={title ? 'img' : undefined}
      focusable="false"
    >
      {title ? <title>{title}</title> : null}
      <path d={d} />
    </svg>
  );
}
